import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { getAuth, getIdToken } from 'firebase/auth';

// Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;

// Circuit breaker configuration constants
const CIRCUIT_BREAKER_THRESHOLD = 5; // Number of failures before circuit opens
const CIRCUIT_BREAKER_RESET_TIMEOUT = 30000; // 30 seconds before trying again

// Circuit breaker state management
interface CircuitBreakerState {
  failures: number;
  lastFailure: number;
  isOpen: boolean;
}

// Create a circuit breaker registry
const circuitBreakers: Record<string, CircuitBreakerState> = {};

// Check if a circuit is open for an endpoint
function isCircuitOpen(endpoint: string): boolean {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const breaker = circuitBreakers[normalizedEndpoint];

  if (!breaker) return false;

  // If the circuit was opened and the timeout has passed, reset it
  if (breaker.isOpen && (Date.now() - breaker.lastFailure > CIRCUIT_BREAKER_RESET_TIMEOUT)) {
    console.log(`[Circuit Breaker] Resetting circuit for ${normalizedEndpoint}`);
    breaker.isOpen = false;
    breaker.failures = 0;
    return false;
  }

  return breaker.isOpen;
}

// Record a failure for an endpoint
function recordFailure(endpoint: string): void {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const breaker = circuitBreakers[normalizedEndpoint] || {
    failures: 0,
    lastFailure: Date.now(),
    isOpen: false
  };

  breaker.failures++;
  breaker.lastFailure = Date.now();

  // Open the circuit if threshold is reached
  if (breaker.failures >= CIRCUIT_BREAKER_THRESHOLD) {
    breaker.isOpen = true;
    console.warn(`[Circuit Breaker] Circuit opened for ${normalizedEndpoint} after ${breaker.failures} failures`);
  }

  circuitBreakers[normalizedEndpoint] = breaker;
}

// Record a success for an endpoint
function recordSuccess(endpoint: string): void {
  const normalizedEndpoint = normalizeEndpoint(endpoint);
  const breaker = circuitBreakers[normalizedEndpoint];

  // If there's an existing breaker with failures, reset them on success
  if (breaker && breaker.failures > 0) {
    breaker.failures = 0;
    breaker.isOpen = false;
    console.log(`[Circuit Breaker] Reset failures for ${normalizedEndpoint} after success`);
  }
}

// Normalize endpoint to group similar requests
function normalizeEndpoint(endpoint: string): string {
  // Group dynamic path parameters (like IDs) to avoid separate circuit breakers
  // Example: /users/123 and /users/456 become /users/:id
  return endpoint
    .replace(/\/\d+([/?#]|$)/g, '/:id$1')
    .replace(/\/[a-f0-9]{24}([/?#]|$)/g, '/:id$1') // MongoDB ObjectIDs
    .replace(/\/[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}([/?#]|$)/g, '/:id$1'); // UUIDs
}

// Error display utility
function showAuthErrorMessage(message: string, isError = true) {
  let errorEl = document.getElementById('auth-error-message');
  if (!errorEl) {
    errorEl = document.createElement('div');
    errorEl.id = 'auth-error-message';
    errorEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${isError ? '#f44336' : '#4caf50'};
      color: white;
      padding: 15px;
      border-radius: 4px;
      z-index: 9999;
      box-shadow: 0 4px 8px rgba(0,0,0,0.2);
      max-width: 350px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    `;
    document.body.appendChild(errorEl);
  }

  errorEl.textContent = message;
  errorEl.style.background = isError ? '#f44336' : '#4caf50';

  // Auto-remove after 5 seconds
  setTimeout(() => {
    if (errorEl && errorEl.parentNode) {
      // Fade out animation
      errorEl.style.transition = 'opacity 0.5s ease';
      errorEl.style.opacity = '0';
      setTimeout(() => {
        if (errorEl?.parentNode) {
          errorEl.parentNode.removeChild(errorEl);
        }
      }, 500);
    }
  }, 5000);
}

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 45000, // Increased timeout
});

// Log the base URL right after creating the instance
console.log(`[API Client] Initialized with Base URL: ${axiosInstance.defaults.baseURL}`);

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  async (config) => {
    const isMockActive = typeof window !== 'undefined' && localStorage.getItem('useMockUser') === 'true';

    if (isMockActive) {
      config.headers.Authorization = 'Bearer mock-token';
      config.headers['X-Mock-User'] = 'true';
    } else {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (currentUser) {
        try {
          // Force refresh might be needed if token expires quickly, but usually not necessary
          const token = await getIdToken(currentUser /*, forceRefresh */);
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            // console.log("[API Interceptor] Token added to request headers."); // Less verbose log
          } else {
            console.warn("[API Interceptor] No auth token available for API request (currentUser exists but token is null).");
          }
        } catch (error) {
          console.error("[API Interceptor] Error getting auth token:", error);
          // Optionally prevent the request if token is essential and failed to get
          // return Promise.reject(new Error("Failed to get authentication token"));
        }
      } else {
        // console.log("[API Interceptor] No current user, request sent without auth token."); // Less verbose log
      }
    }

    // For FormData, let the browser set Content-Type with proper boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    console.error('[API Interceptor] Request setup error:', error); // Log request setup errors
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // --- REDUCED LOG VERBOSITY ---
    // console.log(`[API Interceptor] Success Response Status: ${response.status} for ${response.config.url}`);
    // console.log('[API Interceptor] Success Response Data:', response.data); // Often too verbose
    // console.log('[API Interceptor] Success Response Headers:', response.headers);
    // --- END REDUCED LOG VERBOSITY ---
    return response; // Pass through successful responses
  },
  async (error: AxiosError) => {
    // Log detailed Axios error information
    if (error.response) {
      const isBranches404 = error.response.status === 404 && error.config?.url?.includes('/branches');
      
      if (!isBranches404) {
        console.error(`[API Interceptor] Response Error Status: ${error.response.status} for ${error.config?.url}`);
        // console.error('[API Interceptor] Response Error Data:', error.response.data); // Can be too verbose
        // console.error('[API Interceptor] Response Error Headers:', error.response.headers);
      }

      if (error.response.status === 401) {
        console.error("[API Interceptor] Received 401 Unauthorized. Check token validity and worker/lambda auth.");
        // Potentially trigger logout or token refresh logic here
      }
    } else if (error.request) {
      const online = typeof navigator !== 'undefined' ? navigator.onLine : null;
      const visibility = typeof document !== 'undefined' ? document.visibilityState : null;
      const swController =
        typeof navigator !== 'undefined' && navigator.serviceWorker?.controller
          ? navigator.serviceWorker.controller.scriptURL
          : null;
      console.error(`[API Interceptor] No response received for ${error.config?.url}.`, {
        endpoint: error.config?.url,
        online,
        visibility,
        swController,
      });
    } else {
      console.error('[API Interceptor] Error setting up request:', error.message);
    }
    // console.error('[API Interceptor] Full error config:', error.config); // Kept commented, very verbose

    return Promise.reject(error); // Reject the promise to propagate the error
  }
);

// --- GLOBAL RATE LIMITER ---
const RATE_LIMITS = {
  windowMs: 60 * 1000, // 1 minute
  maxPerWindow: 100,   // Increased from 30 to 100
  burstMs: 1000,       // 1 second
  maxBurst: 20,        // Increased from 5 to 20
};

const endpointRequestLog: Record<string, number[]> = {};

function isRateLimited(endpoint: string): { limited: boolean; message?: string } {
  // Skip rate limiting in development mode to avoid blocking during HMR or frequent reloads
  if (import.meta.env.DEV) {
    return { limited: false };
  }

  const now = Date.now();
  const normalized = normalizeEndpoint(endpoint);
  if (!endpointRequestLog[normalized]) endpointRequestLog[normalized] = [];
  // Remove old timestamps
  endpointRequestLog[normalized] = endpointRequestLog[normalized].filter(ts => now - ts < RATE_LIMITS.windowMs);
  // Check per-minute
  if (endpointRequestLog[normalized].length >= RATE_LIMITS.maxPerWindow) {
    return { limited: true, message: `Too many requests to ${normalized}. Please wait and try again.` };
  }
  // Check burst
  const burst = endpointRequestLog[normalized].filter(ts => now - ts < RATE_LIMITS.burstMs);
  if (burst.length >= RATE_LIMITS.maxBurst) {
    return { limited: true, message: `Too many rapid requests to ${normalized}. Slow down.` };
  }
  // Not limited, record this request
  endpointRequestLog[normalized].push(now);
  return { limited: false };
}

// Main API client hook with circuit breaker
export function useApi() {
  return {
    get: <T>(endpoint: string, params?: Record<string, string | boolean | number>, config?: AxiosRequestConfig) => {
      let fullEndpoint = endpoint;
      if (params && Object.keys(params).length > 0) {
        const queryString = Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
          .join('&');
        fullEndpoint = `${endpoint}${endpoint.includes('?') ? '&' : '?'}${queryString}`;
      }
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      // console.log(`[useApi] Calling GET ${fullEndpoint}`); // Commented out for less verbosity

      if (isCircuitOpen(endpoint)) {
        console.warn(`[Circuit Breaker] Blocking request to ${endpoint} - circuit open`);
        return Promise.reject(new Error(`Circuit breaker open for ${endpoint}`));
      }

      return axiosInstance.get<T>(fullEndpoint, config)
        .then(res => {
          recordSuccess(endpoint);
          // console.log(`[useApi] GET ${fullEndpoint} response data:`, res.data); // Commented out for less verbosity
          return res.data;
        })
        .catch(error => {
          recordFailure(endpoint);
          const isBranches404 = error.response?.status === 404 && endpoint.includes('/branches');
          if (!isBranches404) {
             console.error(`[useApi] GET ${fullEndpoint} failed:`, error);
          }
          throw error;
        });
    },

    post: <T>(endpoint: string, data?: any, config?: AxiosRequestConfig) => {
      // console.log(`[useApi] Calling POST ${endpoint}`); // Commented out for less verbosity
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      if (isCircuitOpen(endpoint)) {
        console.warn(`[Circuit Breaker] Blocking request to ${endpoint} - circuit open`);
        return Promise.reject(new Error(`Circuit breaker open for ${endpoint}`));
      }

      return axiosInstance.post<T>(endpoint, data, config)
        .then(res => {
          recordSuccess(endpoint);
          // console.log(`[useApi] POST ${endpoint} response data:`, res.data); // Commented out for less verbosity
          return res.data;
        })
        .catch(error => {
          recordFailure(endpoint);
          console.error(`[useApi] POST ${endpoint} failed:`, error); // Keep error logs
          throw error;
        });
    },

    put: <T>(endpoint: string, data: any, config?: AxiosRequestConfig) => {
      // console.log(`[useApi] Calling PUT ${endpoint} with data:`, data); // Can be verbose
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      if (isCircuitOpen(endpoint)) {
        console.warn(`[Circuit Breaker] Blocking request to ${endpoint} - circuit open`);
        return Promise.reject(new Error(`Circuit breaker open for ${endpoint}`));
      }

      return axiosInstance.put<T>(endpoint, data, config)
        .then(res => {
          recordSuccess(endpoint);
          // console.log(`[useApi] PUT ${endpoint} response data:`, res.data); // Commented out for less verbosity
          return res.data;
        })
        .catch(error => {
          recordFailure(endpoint);
          console.error(`[useApi] PUT ${endpoint} failed:`, error); // Keep error logs
          throw error;
        });
    },

    patch: <T>(endpoint: string, data: any, config?: AxiosRequestConfig) => {
      // console.log(`[useApi] Calling PATCH ${endpoint} with data:`, data); // Can be verbose
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      if (isCircuitOpen(endpoint)) {
        console.warn(`[Circuit Breaker] Blocking request to ${endpoint} - circuit open`);
        return Promise.reject(new Error(`Circuit breaker open for ${endpoint}`));
      }

      return axiosInstance.patch<T>(endpoint, data, config)
        .then(res => {
          recordSuccess(endpoint);
          // console.log(`[useApi] PATCH ${endpoint} response data:`, res.data); // Commented out for less verbosity
          return res.data;
        })
        .catch(error => {
          recordFailure(endpoint);
          console.error(`[useApi] PATCH ${endpoint} failed:`, error); // Keep error logs
          throw error;
        });
    },

    delete: <T>(endpoint: string, config?: AxiosRequestConfig) => {
      // console.log(`[useApi] Calling DELETE ${endpoint}`); // Commented out for less verbosity
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      if (isCircuitOpen(endpoint)) {
        console.warn(`[Circuit Breaker] Blocking request to ${endpoint} - circuit open`);
        return Promise.reject(new Error(`Circuit breaker open for ${endpoint}`));
      }

      return axiosInstance.delete<T>(endpoint, config)
        .then(res => {
          recordSuccess(endpoint);
          // console.log(`[useApi] DELETE ${endpoint} response data:`, res.data); // Commented out for less verbosity
          return res.data;
        })
        .catch(error => {
          recordFailure(endpoint);
          console.error(`[useApi] DELETE ${endpoint} failed:`, error); // Keep error logs
          throw error;
        });
    },

    bypassCache: <T>(endpoint: string, config?: AxiosRequestConfig) => {
      // console.log(`[useApi] Calling GET ${endpoint} with cache bypass`); // Commented out for less verbosity
      // --- RATE LIMIT CHECK ---
      const rl = isRateLimited(endpoint);
      if (rl.limited) {
        showAuthErrorMessage(rl.message || 'Rate limit exceeded.');
        return Promise.reject(new Error(rl.message || 'Rate limit exceeded.'));
      }

      const configWithHeaders = {
        ...config,
        headers: {
          ...(config?.headers || {}),
          'X-Cache-Bypass': 'true'
        }
      };

      return axiosInstance.get<T>(endpoint, configWithHeaders)
        .then(res => res.data);
    },

    resetCircuitBreaker: (endpoint: string) => {
      const normalizedEndpoint = normalizeEndpoint(endpoint);
      if (circuitBreakers[normalizedEndpoint]) {
        circuitBreakers[normalizedEndpoint].failures = 0;
        circuitBreakers[normalizedEndpoint].isOpen = false;
        console.log(`[Circuit Breaker] Manually reset circuit for ${normalizedEndpoint}`);
        return true;
      }
      return false;
    },

    getCircuitStatus: (endpoint: string) => {
      const normalizedEndpoint = normalizeEndpoint(endpoint);
      const breaker = circuitBreakers[normalizedEndpoint];
      if (!breaker) return { exists: false };

      return {
        exists: true,
        isOpen: breaker.isOpen,
        failures: breaker.failures,
        lastFailure: breaker.lastFailure,
        timeUntilReset: breaker.isOpen ?
          Math.max(0, CIRCUIT_BREAKER_RESET_TIMEOUT - (Date.now() - breaker.lastFailure)) :
          0
      };
    },

    axiosInstance,
    showError: (message: string) => showAuthErrorMessage(message, true),
    showSuccess: (message: string) => showAuthErrorMessage(message, false)
  };
}

// Optional: Export the instance directly if needed elsewhere without the hook
export default axiosInstance;
